"use client";

import { useCart } from "@/hooks/useCart";
import { useWishlist } from "@/hooks/useWishlist";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { cn, formatPrice } from "@/lib/utils";
import type { StoreProduct } from "@medusajs/types";
import { useCheckoutStore } from "@/stores/checkout-store";

type OrderSummaryFooterProps = {
  className?: string;
  locale: Locales;
  medusaProducts: Array<StoreProduct>;
};

const WishlistOrderSummaryFooter = ({ className, locale, medusaProducts }: OrderSummaryFooterProps) => {
  const { cart } = useCart();
  const { lists, activeListName } = useWishlist();
  // For multi-variant products we need to know which surface is currently
  // selected per product so we can match the right variant and price.
  // WishlistProductCard seeds the store on mount (even before the modal is
  // opened), so productCustomizations is always populated for range products.
  const productCustomizations = useCheckoutStore((s) => s.productCustomizations);

  const activeList = activeListName ? lists?.find((list) => list.listName === activeListName) : lists![0];
  const activeListId = activeList?.id;
  const productsInActiveList = activeList?.products || [];

  const itemsTotal = productsInActiveList.reduce((acc, wishlistProduct) => {
    const productId = wishlistProduct.productId;
    const product = medusaProducts.find((p) => p.id === productId);
    if (!product) return acc;

    const hasMultipleVariants = (product.variants?.length ?? 0) > 1;

    //  Resolve which variant is "selected" for this product
    // For multi-variant: use the stored surface to find the selected variant.
    // For single-variant: just use variants[0].
    const selectedVariant = hasMultipleVariants
      ? (() => {
          const storedSurface = productCustomizations[productId]?.surface;
          return product.variants?.find((v) => v.metadata?.["surface"] == storedSurface) ?? product.variants?.[0];
        })()
      : product.variants?.[0];

    // ── isInCart: must match the SPECIFIC selected variant + this wishlist ─
    // If user switches variant A→B, A is still in cart but should not count.
    const cartItem = selectedVariant ? cart?.items?.find((item) => item.variant_id === selectedVariant.id) : undefined;

    const isInCart = Boolean(
      cartItem &&
      activeListId &&
      Array.isArray(cartItem.metadata?.wishlist_ids) &&
      cartItem.metadata.wishlist_ids.includes(activeListId)
    );

    let itemTotal = 0;
    if (isInCart && cartItem) {
      // Use actual cart unit_price (what was charged) × cart quantity
      itemTotal = (cartItem.unit_price || 0) * (cartItem.quantity || 1);
    } else {
      // Not in cart: use the resolved variant's catalog price × wishlist quantity
      const quantity = wishlistProduct.quantity || 1;
      const unitPrice = selectedVariant?.calculated_price?.calculated_amount ?? 0;
      itemTotal = unitPrice * quantity;
    }

    return acc + itemTotal;
  }, 0);

  const priceWithTax = itemsTotal * 1.21;

  return (
    <dl className={cn("border-primary-light/50 border-t pt-5", className)}>
      <div className="flex items-center justify-between">
        <dd className="text-primary-light">{__("Total VAT exc.", locale)}</dd>
        <dt>{formatPrice(itemsTotal || 0)}</dt>
      </div>
      <div className="flex items-center justify-between">
        <dd className="text-primary-light">{__("VAT (21%)", locale)}</dd>
        <dt>{formatPrice(priceWithTax - itemsTotal)}</dt>
      </div>
      <hr className="border-primary-light/50 my-5" />
      <div className="flex items-start justify-between">
        <dd className="leading-h1 text-lg font-semibold">
          {__("Total VAT", locale)}* <br />
          <span className="text-primary-light text-xs font-normal">
            *{__("Shipping costs not included", locale)} ...
          </span>
        </dd>
        <dt className="text-xl font-semibold">{formatPrice(priceWithTax || 0)}</dt>
      </div>
    </dl>
  );
};

export default WishlistOrderSummaryFooter;
